// components/MindmapSection.js - Version corrigée avec expand/collapse simplifié
import { RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
    addEdge,
    Background,
    Controls,
    useEdgesState,
    useNodesState,
    MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useMindmapDataCached } from '../../../hooks-back/useMindmapDataCached';
import MindmapFilters from './MindmapFilters';

const MindmapSection = ({
    showFilters = false,
    className = "",
    data,
    hooks,
    onGlobalRefresh,
    filterProps
}) => {
    // État pour gérer les nœuds développés/fermés
    const [expandedNodes, setExpandedNodes] = useState(new Set());

    // Extraire les props de filtres si disponibles
    const {
        filterByYear,
        filterByCategory,
        filterByStatus,
        onFilterYearChange,
        onFilterCategoryChange,
        onFilterStatusChange
    } = filterProps || {};

    // États des filtres locaux (fallback si pas de filterProps)
    const [localFilterByYear, setLocalFilterByYear] = useState('all');
    const [localFilterByCategory, setLocalFilterByCategory] = useState('all');
    const [localFilterByStatus, setLocalFilterByStatus] = useState('all');

    // Utiliser les filtres centralisés ou locaux
    const activeFilterByYear = filterByYear || localFilterByYear;
    const activeFilterByCategory = filterByCategory || localFilterByCategory;
    const activeFilterByStatus = filterByStatus || localFilterByStatus;

    // Hook avec données centralisées
    const {
        mindmapData,
        availableYears,
        availableCategories,
        availableStatuses,
        isLoading,
        hasError,
        error,
        handleRefresh,
        handleNodeClick,
        isFromCache
    } = useMindmapDataCached(data, hooks);

    // Fonction pour déterminer si un nœud a des enfants directs
    const hasChildren = useCallback((nodeId, allEdges) => {
        return allEdges.some(edge => edge.source === nodeId);
    }, []);

    // Fonction pour basculer l'état d'expansion d'un nœud
    const toggleNodeExpansion = useCallback((nodeId) => {
        setExpandedNodes(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(nodeId)) {
                newExpanded.delete(nodeId);
            } else {
                newExpanded.add(nodeId);
            }
            return newExpanded;
        });
    }, []);

    // Initialiser les nœuds développés (par défaut, seuls les nœuds racine sont visibles)
    useEffect(() => {
        if (mindmapData.nodes.length > 0) {
            // Identifier les nœuds racine (sans parent)
            const rootNodes = mindmapData.nodes.filter(node => 
                !mindmapData.edges.some(edge => edge.target === node.id)
            );
            // Par défaut, ne développer aucun nœud (même pas les racines)
            // Les nœuds racine restent visibles mais fermés
            setExpandedNodes(new Set());
        }
    }, [mindmapData]);

    // Filtrage des nœuds et edges avec logique d'expansion
    const filteredData = useMemo(() => {
        // D'abord filtrer selon les critères habituels
        const filteredNodes = mindmapData.nodes.filter(node => {
            // FILTRAGE PAR ANNÉE
            if (activeFilterByYear !== 'all') {
                if (node.id.startsWith('objectif-')) {
                    return true;
                }
                if (node.year && node.year !== 'all' && node.year !== 'no-date') {
                    if (node.year !== activeFilterByYear) {
                        return false;
                    }
                } else if (node.year === 'no-date') {
                    return false;
                }
            }

            // FILTRAGE PAR CATÉGORIE
            if (activeFilterByCategory !== 'all') {
                if (!node.category || node.category !== activeFilterByCategory) {
                    return false;
                }
            }

            // FILTRAGE PAR STATUT
            if (activeFilterByStatus !== 'all') {
                if (!node.status || node.status !== activeFilterByStatus) {
                    return false;
                }
            }

            return true;
        });

        // Ensuite appliquer la logique d'expansion
        const visibleNodes = filteredNodes.filter(node => {
            // Les nœuds racine sont toujours visibles
            const isRootNode = !mindmapData.edges.some(edge => edge.target === node.id);
            if (isRootNode) {
                return true;
            }

            // Pour les nœuds enfants, vérifier si leur parent est développé
            const parentEdge = mindmapData.edges.find(edge => edge.target === node.id);
            if (!parentEdge) return true;

            // Vérifier que le parent direct est développé
            return expandedNodes.has(parentEdge.source);
        });

        // Ajouter les métadonnées d'expansion aux nœuds
        const nodesWithExpandIndicators = visibleNodes.map(node => {
            const nodeHasChildren = hasChildren(node.id, mindmapData.edges);
            const isExpanded = expandedNodes.has(node.id);
            
            return {
                ...node,
                data: {
                    ...node.data,
                    hasChildren: nodeHasChildren,
                    isExpanded,
                    expandIcon: isExpanded ? ChevronDown : ChevronRight
                }
            };
        });

        // Filtrer les edges pour ne garder que ceux entre nœuds visibles
        const visibleNodeIds = new Set(visibleNodes.map(node => node.id));
        const filteredEdges = mindmapData.edges.filter(edge =>
            visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
        );

        const edgesWithDefaultType = filteredEdges.map(edge => ({
            ...edge,
            type: edge.type || 'smoothstep',
        }));

        return { nodes: nodesWithExpandIndicators, edges: edgesWithDefaultType };
    }, [mindmapData, activeFilterByYear, activeFilterByCategory, activeFilterByStatus, expandedNodes, hasChildren]);

    const [nodes, setNodes, onNodesChange] = useNodesState(filteredData.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(filteredData.edges);

    // Fonctions de gestion des filtres avec fallback
    const handleFilterYearChange = useCallback((year) => {
        if (onFilterYearChange) {
            onFilterYearChange(year);
        } else {
            setLocalFilterByYear(year);
        }
    }, [onFilterYearChange]);

    const handleFilterCategoryChange = useCallback((category) => {
        if (onFilterCategoryChange) {
            onFilterCategoryChange(category);
        } else {
            setLocalFilterByCategory(category);
        }
    }, [onFilterCategoryChange]);

    const handleFilterStatusChange = useCallback((status) => {
        if (onFilterStatusChange) {
            onFilterStatusChange(status);
        } else {
            setLocalFilterByStatus(status);
        }
    }, [onFilterStatusChange]);

    const handleFilterReset = useCallback(() => {
        if (onFilterYearChange) {
            onFilterYearChange('all');
            onFilterCategoryChange('all');
            onFilterStatusChange('all');
        } else {
            setLocalFilterByYear('all');
            setLocalFilterByCategory('all');
            setLocalFilterByStatus('all');
        }
    }, [onFilterYearChange, onFilterCategoryChange, onFilterStatusChange]);

    const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

    // Gestion du clic sur le background
    const onPaneClick = useCallback((event) => {
        if (handleNodeClick) {
            handleNodeClick('background');
        }
    }, [handleNodeClick]);

    // Gestion du clic sur les nœuds - VERSION SIMPLE
    const onNodeClick = useCallback((event, node) => {
        event.preventDefault();
        event.stopPropagation();

        // Si le nœud a des enfants, basculer son état d'expansion
        if (node.data.hasChildren) {
            toggleNodeExpansion(node.id);
        }

        // Appeler le handler original si disponible
        if (handleNodeClick) {
            handleNodeClick(node.id);
        }
    }, [handleNodeClick, toggleNodeExpansion]);

    // Mettre à jour les nœuds et edges quand les données changent
    useEffect(() => {
        setNodes(filteredData.nodes);
        setEdges(filteredData.edges);
    }, [filteredData, setNodes, setEdges]);

    // Fonctions utilitaires pour contrôler l'expansion
    const expandAll = useCallback(() => {
        const allNodeIds = mindmapData.nodes.map(node => node.id);
        setExpandedNodes(new Set(allNodeIds));
    }, [mindmapData.nodes]);

    const collapseAll = useCallback(() => {
        const rootNodes = mindmapData.nodes.filter(node => 
            !mindmapData.edges.some(edge => edge.target === node.id)
        );
        setExpandedNodes(new Set(rootNodes.map(node => node.id)));
    }, [mindmapData.nodes, mindmapData.edges]);

    // Vérification de sécurité
    if (!data || (!data.objectifs && !data.plans)) {
        return (
            <div className={`h-full flex items-center justify-center ${className}`}>
                <div className="text-center text-gray-500">
                    <p>Données centralisées non fournies</p>
                </div>
            </div>
        );
    }

    // Handler pour rafraîchissement
    const handleMindmapRefresh = () => {
        console.log('🔄 Rafraîchissement Mindmap');
        if (onGlobalRefresh && typeof onGlobalRefresh === 'function') {
            onGlobalRefresh();
        } else if (handleRefresh && typeof handleRefresh === 'function') {
            handleRefresh();
        }
    };

    // Gestion du chargement
    if (isLoading) {
        return (
            <div className={`h-full flex items-center justify-center ${className}`}>
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-2" />
                    <div className="text-gray-600">Chargement de la mindmap...</div>
                    {isFromCache && (
                        <div className="text-xs text-blue-500 mt-1">Données depuis le cache</div>
                    )}
                </div>
            </div>
        );
    }

    // Gestion des erreurs
    if (hasError) {
        return (
            <div className={`h-full flex items-center justify-center ${className}`}>
                <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
                    <div className="text-red-700 font-medium mb-2">Erreur de chargement</div>
                    <div className="text-red-600 text-sm mb-4">{error}</div>
                    <button
                        onClick={handleMindmapRefresh}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`h-full flex flex-col ${className}`}>
            {/* Panneau de filtres avec contrôles d'expansion */}
            {showFilters && (
                <div className="flex flex-col gap-2">
                    <MindmapFilters
                        filterByYear={activeFilterByYear}
                        filterByCategory={activeFilterByCategory}
                        filterByStatus={activeFilterByStatus}
                        availableYears={availableYears}
                        availableCategories={availableCategories}
                        availableStatuses={availableStatuses}
                        onFilterYearChange={handleFilterYearChange}
                        onFilterCategoryChange={handleFilterCategoryChange}
                        onFilterStatusChange={handleFilterStatusChange}
                        onReset={handleFilterReset}
                        onRefresh={handleMindmapRefresh}
                        resultCount={filteredData.nodes.length}
                        isLoading={isLoading}
                        isFromCache={isFromCache}
                    />
                    
              
                </div>
            )}

            {/* Message d'état vide */}
            {filteredData.nodes.length === 0 && !isLoading && (
                <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                        <div className="text-lg font-medium mb-2">Aucun élément trouvé</div>
                        <div className="text-sm mb-4">
                            {mindmapData.nodes.length === 0
                                ? "Aucun objectif ou plan d'action disponible"
                                : "Essayez de modifier vos filtres"
                            }
                        </div>
                        {mindmapData.nodes.length === 0 ? (
                            <button
                                onClick={handleMindmapRefresh}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                            >
                                Actualiser
                            </button>
                        ) : (
                            <button
                                onClick={handleFilterReset}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                            >
                                Réinitialiser les filtres
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Mindmap */}
            {filteredData.nodes.length > 0 && (
                <div className="flex-1 overflow-hidden" style={{ width: '100%', height: '100%' }}>
                    <div style={{ width: '100%', height: '500px', minHeight: '500px' }}>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onNodeClick={onNodeClick}
                            onPaneClick={onPaneClick}
                            fitView
                            fitViewOptions={{
                                padding: 0.2,
                                includeHiddenNodes: false,
                                minZoom: 0.3,
                                maxZoom: 1.5
                            }}
                            attributionPosition="bottom-left"
                            proOptions={{ hideAttribution: true }}
                            defaultViewport={{ x: 0, y: 0, zoom: 1.2 }}
                            minZoom={0.2}
                            maxZoom={2.0}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable={true}
                            zoomOnDoubleClick={false}
                            elevateEdgesOnSelect={true}
                            snapToGrid={true}
                            snapGrid={[15, 15]}
                        >
                            <Controls
                                position="top-right"
                                style={{
                                    backgroundColor: '#f0fdf4',
                                    border: '2px solid #16a34a',
                                    borderRadius: '8px'
                                }}
                            />
                            <Background
                                variant="dots"
                                gap={15}
                                size={1}
                                color="#16a34a"
                                style={{ backgroundColor: '#fefffe' }}
                            />
                        </ReactFlow>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MindmapSection;